import { Button, Col, Divider, Form, FormInstance, FormListOperation, FormProps, Row, Space, Tag, theme } from 'antd';
import { FC, ReactElement } from 'react';
import { CheckCircleFill, ExclamationCircleFill, Plus, XCircleFill } from 'react-bootstrap-icons';

import { ButtonSuccess } from '@/common/components';
import { ArrayElement, FormFieldsErrors, FormValidationSchema } from '@/common/interfaces';

import {
  ReferralLinksFormFields,
  ReferralLinksFormInputs,
  ReferralLinksLimit,
  ReferralLinksListComponent,
} from '@/modules/Sites';

import { ReferralLinksEditFormViewItem } from './ReferralLinksEditFormViewItem';

export interface ReferralLinksEditFormViewProps {
  form: FormInstance<ReferralLinksFormFields>;
  formErrors: { index: number; field: string; message: string } | null;
  initialValues: ReferralLinksFormFields;
  isFormLoading: boolean;
  validationSchema: FormValidationSchema<ReferralLinksFormFields>;
  handleBack: () => void;
  handleFinish: FormProps<ReferralLinksFormFields>['onFinish'];
  handleFinishFailed: FormProps<ReferralLinksFormFields>['onFinishFailed'];
  handleSetEditingListMode: (is?: true) => void;

  siteId: string;
  referralId: string;
}

export interface FormListOperationsType extends Omit<FormListOperation, 'add'> {
  add: (defaultValue?: Partial<ArrayElement<ReferralLinksFormFields['links']>>, insertIndex?: number | undefined) => void;
}

export const ReferralLinksEditFormView: FC<ReferralLinksEditFormViewProps> = ({
  form,
  formErrors,
  initialValues,
  isFormLoading,
  validationSchema,
  siteId,
  referralId,
  handleBack,
  handleFinish,
  handleFinishFailed,
  handleSetEditingListMode,
}) => {
  const {
    token: { colorError, colorSuccess },
  } = theme.useToken();

  const commonFrequency = +Form.useWatch(
    (v) => v.links?.reduce((acc, { appearanceFrequency }) => appearanceFrequency + acc, 0),
    form,
  )?.toFixed(2);

  const handleAddItem = (addFn: FormListOperationsType['add']) => {
    addFn(
      {
        id: undefined,
        referralId,
        isActive: true,
        isNew: true,
        url: undefined,
        appearanceFrequency: 0,
        isFrequencyLocked: false,
        isRemoved: false,
        siteId,
      },
      0,
    );
  };

  return (
    <Form
      form={form}
      name="referral-links-list-form"
      initialValues={initialValues}
      disabled={isFormLoading}
      autoComplete="off"
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
    >
      <Form.List name={ReferralLinksFormInputs.Links}>
        {(fields, { add, remove }: FormListOperationsType): ReactElement => (
          <>
            <Button
              block
              type="dashed"
              icon={<Plus />}
              onClick={() => handleAddItem(add)}
              disabled={isFormLoading || fields.length >= ReferralLinksLimit}
            >
              Додати посилання
            </Button>

            <ReferralLinksListComponent dataSource={fields} isButtonActive onButtonClick={handleBack} isEditingList />

            {fields.map((item, idx) => {
              return (
                <div style={{ marginTop: 12 }} key={idx}>
                  <ReferralLinksEditFormViewItem
                    form={form}
                    isFormLoading={isFormLoading}
                    item={item}
                    initialValues={initialValues}
                    remove={remove}
                    formError={formErrors?.index === idx ? formErrors : null}
                  />
                  <Divider style={{ marginTop: 12, marginBottom: 20 }} />
                </div>
              );
            })}
          </>
        )}
      </Form.List>

      <Form.Item wrapperCol={{ span: 24 }} noStyle>
        <Row gutter={[16, 16]} align="middle">
          <Col flex={'auto'}>
            <Tag>
              {commonFrequency === 100 ? <CheckCircleFill color={colorSuccess} /> : <ExclamationCircleFill color={colorError} />}
              <span>{commonFrequency}%</span>
            </Tag>
          </Col>
          <Col span={14}>
            <ButtonSuccess
              block
              loading={isFormLoading}
              disabled={isFormLoading || commonFrequency !== 100}
              type="primary"
              htmlType="submit"
            >
              Зберегти
            </ButtonSuccess>
          </Col>
          <Col>
            <Button type="default" loading={isFormLoading} disabled={isFormLoading} onClick={handleBack} icon={<XCircleFill />} />
          </Col>
        </Row>
      </Form.Item>
    </Form>
  );
};
